import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useToast } from "../context/ToastContext";

const Navbar = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      success("Logged out successfully");
      navigate("/");
    } catch (err) {
      error("Failed to log out");
    }
  };

  return (
    <nav className={`navbar ${isScrolled ? "navbar-scrolled" : ""}`}>
      <div className="container">
        <div className="navbar-content">
          {/* Logo */}
          <Link to="/" className="navbar-brand">
            <img src="/logo.png" alt="Logo" className="brand-logo" />
            <span className="brand-text">Kumbhar Samaj Onnati Mandal</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="navbar-links desktop-only">
            <NavLink to="/" className="nav-link" end>
              Home
            </NavLink>
            <NavLink to="/matrimony" className="nav-link">
              Matrimony
            </NavLink>
            <NavLink to="/news" className="nav-link">
              News
            </NavLink>
          </div>

          {/* Auth Actions */}
          <div className="navbar-actions desktop-only">
            {currentUser ? (
              <div className="user-menu">
                <button className="user-menu-trigger">
                  <div className="avatar">
                    {currentUser.displayName?.charAt(0) || "U"}
                  </div>
                  <span>{currentUser.displayName || "User"}</span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M8 11L3 6h10l-5 5z" />
                  </svg>
                </button>
                <div className="user-menu-dropdown">
                  <Link to="/matrimony/create" className="dropdown-item">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    My Profile
                  </Link>
                  {currentUser?.isAdmin && (
                    <>
                      <div className="dropdown-divider"></div>
                      <Link
                        to="/admin"
                        className="dropdown-item dropdown-item--admin"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M12 2L2 7l10 5 10-5-10-5z" />
                          <path d="M2 17l10 5 10-5" />
                          <path d="M2 12l10 5 10-5" />
                        </svg>
                        Admin Panel
                      </Link>
                    </>
                  )}
                  <button onClick={handleLogout} className="dropdown-item">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="auth-buttons">
                <NavLink to="/login" className="nav-link">
                  Login
                </NavLink>

                <Link to="/register" className="btn btn-primary btn-sm">
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle mobile-only"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="mobile-menu">
            <NavLink
              to="/"
              className="mobile-nav-link"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </NavLink>
            <NavLink
              to="/matrimony"
              className="mobile-nav-link"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Matrimony
            </NavLink>
            <NavLink
              to="/news"
              className="mobile-nav-link"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              News
            </NavLink>

            {currentUser ? (
              <>
                <NavLink
                  to="/create-profile"
                  className="mobile-nav-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Profile
                </NavLink>
                <button
                  onClick={handleLogout}
                  className="mobile-nav-link"
                  style={{ width: "100%", textAlign: "left" }}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <NavLink
                  to="/login"
                  className="mobile-nav-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Login
                </NavLink>
                <NavLink
                  to="/register"
                  className="mobile-nav-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Register
                </NavLink>
              </>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .navbar {
          position: sticky;
          top: 0;
          left: 0;
          right: 0;
          z-index: var(--z-sticky);
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--neutral-200);
          transition: all var(--transition-base);
        }

        .navbar-scrolled {
          box-shadow: var(--shadow-sm);
        }

        .navbar-content {
          display: flex;
          align-items: center;
          padding: 1rem 0;
        }

        .navbar-brand {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-family: var(--font-display);
          font-size: var(--text-xl);
          font-weight: var(--weight-bold);
          color: var(--primary-900);
          text-decoration: none;
          margin-right: 3rem;
          flex-shrink: 0;
        }

        .brand-logo {
          width: 50px;
          height: 50px;
          object-fit: cover;
          border-radius: 50%;
          border: 2px solid #d6af36;
          background: white;
        }
        .navbar-links {
          display: flex;
          align-items: center;
          gap: 2rem;
        }

        .nav-link {
          font-size: var(--text-base);
          font-weight: var(--weight-medium);
          color: var(--neutral-700);
          text-decoration: none;
          padding: 0.75rem 1rem;
          border-radius: var(--radius-md);
          transition: all var(--transition-fast);
          white-space: nowrap;
        }

        .nav-link:hover {
          color: var(--primary-500);
          background: var(--neutral-100);
        }

        .nav-link.active {
          color: var(--primary-500);
          font-weight: var(--weight-semibold);
        }

        .navbar-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-left: 1.5rem;
        }

        .auth-buttons {
          display: flex;
          gap: var(--space-2);
        }

        .user-menu {
          position: relative;
        }

        .user-menu-trigger {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-2) var(--space-3);
          background: var(--neutral-100);
          border: none;
          border-radius: var(--radius-lg);
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .user-menu-trigger:hover {
          background: var(--neutral-200);
        }

        .avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--primary-500);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: var(--weight-bold);
          font-size: var(--text-sm);
        }

        .user-menu-dropdown {
          position: absolute;
          top: calc(100% + var(--space-2));
          right: 0;
          background: white;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--neutral-200);
          min-width: 200px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all var(--transition-base);
        }

        .user-menu:hover .user-menu-dropdown {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          width: 100%;
          padding: var(--space-3) var(--space-4);
          background: none;
          border: none;
          color: var(--neutral-700);
          font-size: var(--text-sm);
          font-weight: var(--weight-medium);
          text-decoration: none;
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .dropdown-item:hover {
          background: var(--neutral-100);
          color: var(--primary-500);
        }

        .dropdown-item:first-child {
          border-radius: var(--radius-lg) var(--radius-lg) 0 0;
        }

        .dropdown-item:last-child {
          border-radius: 0 0 var(--radius-lg) var(--radius-lg);
        }
        .dropdown-item--admin {
          color: var(--primary-600);
          font-weight: var(--weight-semibold);
          background: var(--primary-50);
        }
        .dropdown-item--admin:hover {
          background: var(--primary-100);
          color: var(--primary-800);
        }
        .mobile-menu-toggle {
          display: none;
          flex-direction: column;
          gap: 4px;
          background: none;
          border: none;
          cursor: pointer;
          padding: var(--space-2);
        }

        .mobile-menu-toggle span {
          display: block;
          width: 24px;
          height: 2px;
          background: var(--neutral-700);
          transition: all var(--transition-fast);
        }

        .mobile-menu {
          display: none;
          flex-direction: column;
          gap: var(--space-2);
          padding: var(--space-4) 0;
          border-top: 1px solid var(--neutral-200);
          margin-top: var(--space-4);
        }

        .mobile-nav-link {
          padding: var(--space-3);
          color: var(--neutral-700);
          font-weight: var(--weight-medium);
          text-decoration: none;
          border-radius: var(--radius-md);
          transition: all var(--transition-fast);
          background: none;
          border: none;
          cursor: pointer;
        }

        .mobile-nav-link:hover,
        .mobile-nav-link.active {
          background: var(--neutral-100);
          color: var(--primary-500);
        }

        .desktop-only {
          display: flex;
        }

        .mobile-only {
          display: none;
        }

        @media (max-width: 768px) {
          .desktop-only {
            display: none;
          }

          .mobile-only {
            display: flex;
          }

          .mobile-menu {
            display: flex;
          }

          .brand-text {
            font-size: var(--text-base);
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
